import type { ReactNode } from "react"

import { FooterFacebookIcon, FooterInstagramIcon, FooterLinkedInIcon, FooterTikTokIcon, FooterYouTubeIcon } from "@/components/ui"
import { LocalizedClientLink } from "@/components/ui/localized-client-link"
import { policyPath } from "@/lib/legal-policies"
import { buildRoute, ROUTES } from "@/lib/routes"

import type { getTranslations } from "next-intl/server"

type FooterTranslator = Awaited<ReturnType<typeof getTranslations<"footer">>>

interface FooterLink {
  label: string
  href: string
}

export interface FooterColumn {
  heading: string
  links: FooterLink[]
}

const NELO_SITE = "https://nelo.mx"

const siteFor =
  (locale: string) =>
  (path: string): string =>
    `${NELO_SITE}/${locale.slice(0, 2)}${path}`

const getPolicyLinks = (t: FooterTranslator): FooterLink[] => [
  { label: t("termsAndConditions"), href: policyPath("terms") },
  { label: t("privacy"), href: policyPath("privacy") },
  { label: t("warrantyPolicy"), href: policyPath("warranty-policy") },
  { label: t("returnPolicy"), href: policyPath("return-policy") },
  { label: t("officialStores"), href: policyPath("official-stores-policy") },
]

const SHOP_CATEGORIES = [
  ["perfumes", "salud-y-belleza/belleza/fragancias"],
  ["menPerfumes", "salud-y-belleza/belleza/fragancias/fragancias-para-hombre"],
  ["womenPerfumes", "salud-y-belleza/belleza/fragancias/fragancias-para-mujer"],
  ["womenSneakers", "ropa-y-accesorios/zapatos-y-calzado/calzado-de-mujer/calzado-deportivo-mujer"],
  ["menSneakers", "ropa-y-accesorios/zapatos-y-calzado/calzado-para-hombre/calzado-deportivo-hombre"],
  ["menBoots", "ropa-y-accesorios/zapatos-y-calzado/calzado-para-hombre/botas-hombre"],
  ["smartwatches", "electronica/celulares-y-relojes-inteligentes/relojes-inteligentes"],
  ["headphonesAndSpeakers", "electronica/reproductores-de-audio-y-video-portatiles"],
  ["phones", "electronica/celulares-y-relojes-inteligentes/celulares"],
  ["smallAppliances", "casa/articulos-para-el-hogar/cocina-y-comedor/electrodomesticos-pequenos"],
  ["cookware", "casa/articulos-para-el-hogar/cocina-y-comedor/baterias-de-cocina"],
  ["backpacks", "viajes-y-equipaje/mochilas"],
] as const

export const getShopCategoryLinks = (t: FooterTranslator): FooterLink[] => [
  ...SHOP_CATEGORIES.map(([key, path]) => ({ label: t(`shopCategories.${key}`), href: buildRoute.category(path) })),
  { label: t("shopCategories.allCategories"), href: buildRoute.category() },
]

// The four groups and their order follow the Figma footer (1063:6132 / 311:11541).
export const getFooterColumns = (t: FooterTranslator, locale: string): FooterColumn[] => {
  const site = siteFor(locale)
  return [
    {
      heading: t("aboutColumnHeading"),
      links: [
        { label: t("creditCard"), href: site("/tarjeta-de-credito") },
        { label: t("onlineLoans"), href: site("/prestamos-en-linea") },
        { label: t("loansWithoutBureau"), href: site("/prestamos-sin-buro") },
        { label: t("personalLoans"), href: site("/prestamos-personales") },
        { label: t("personalCredit"), href: site("/creditos-personales") },
        { label: t("loansInYourCity"), href: site("/prestamos-ciudad") },
        { label: t("servicesAndTopUps"), href: site("/todas-las-tiendas?categoria=Servicios%20y%20Recargas") },
        { label: "Tienda Nelo", href: "/" },
        { label: t("sellOnTiendaNelo"), href: `/${ROUTES.FOR_MERCHANTS}` },
        { label: "Nelo VIP", href: site("/vip") },
        { label: t("workAtNelo"), href: site("/trabaja-en-nelo") },
      ],
    },
    {
      heading: t("storesColumnHeading"),
      links: [
        { label: "Amazon", href: site("/tiendas/amazon") },
        { label: "SHEIN", href: site("/tiendas/shein") },
        { label: "Walmart", href: site("/tiendas/walmart") },
        { label: "Telcel", href: site("/servicios/telcel") },
        { label: "CFE", href: site("/servicios/cfe") },
        { label: t("allStores"), href: site("/todas-las-tiendas") },
        { label: t("allServices"), href: site("/servicios") },
      ],
    },
    {
      heading: t("legalColumnHeading"),
      links: [...getPolicyLinks(t), { label: t("arcoRights"), href: site("/legal/derechos-arco") }, { label: t("legalDocuments"), href: site("/legal/documentos-legales") }],
    },
    {
      heading: t("contactColumnHeading"),
      links: [
        { label: "WhatsApp", href: `${NELO_SITE}/whatsapp` },
        { label: "hola@nelo.mx", href: "mailto:hola@nelo.mx" },
        { label: t("faq"), href: site("/preguntas-frecuentes") },
      ],
    },
  ]
}

export const FOOTER_SOCIAL: { label: string; href: string; icon: ReactNode }[] = [
  { label: "Facebook", href: "https://www.facebook.com/Nelo.mx/", icon: <FooterFacebookIcon /> },
  { label: "Instagram", href: "https://www.instagram.com/nelo.mx/", icon: <FooterInstagramIcon /> },
  { label: "TikTok", href: "https://www.tiktok.com/@nelo.mx", icon: <FooterTikTokIcon /> },
  { label: "YouTube", href: "https://www.youtube.com/@nelomexico", icon: <FooterYouTubeIcon /> },
  { label: "LinkedIn", href: "https://www.linkedin.com/company/nelo-mobile/mycompany/", icon: <FooterLinkedInIcon /> },
]

export const APP_STORE_LINKS = {
  ios: "https://apps.apple.com/mx/app/nelo/id1477987798",
  android: "https://play.google.com/store/apps/details?id=co.nelo.nelo&gl=MX",
}

export const LEGAL_DISCLOSURE = [
  "Nelo Mobile, S.A. de C.V., se encuentra regulado por la Procuraduría Federal de Consumidor y sujeto a la supervisión de la Secretaría de Hacienda y Crédito Público a través del Servicio de Administración Tributaria, para efectos de lo dispuesto por el artículo 17 fracción IV de la Ley Federal para la Prevención e Identificación de Operaciones con Recursos de Procedencia Ilícita.",
  "Todos los precios en Tienda.Nelo.Mx son en pesos mexicanos. Los precios y promociones de nuestro sitio web son exclusivos de Tienda.Nelo.Mx. Los abonos quincenales, plazos o el pago inicial pueden variar según el margen de crédito y el historial de pago de cada cliente. Recuerda cumplir los siguientes requisitos: ser mayor de edad, ser de nacionalidad mexicana, domicilio en territorio nacional y credencial para votar original vigente.",
]

export const FooterNavLink = ({ link, className }: { link: FooterLink; className?: string }) => {
  if (link.href.startsWith("/")) {
    return (
      <LocalizedClientLink href={link.href} className={className}>
        {link.label}
      </LocalizedClientLink>
    )
  }

  const newTab = link.href.startsWith("http")
  return (
    <a href={link.href} target={newTab ? "_blank" : undefined} rel={newTab ? "noreferrer" : undefined} className={className}>
      {link.label}
    </a>
  )
}
